import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { 
  User, 
  Key, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  CheckCircle,
  Shield
} from 'lucide-react';
import { CalendarSyncCard } from './CalendarSync';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = () => {
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  
  // Profile edit state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    work_phone: user?.work_phone || '',
    home_phone: user?.home_phone || ''
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });
  const [passwordRequirements, setPasswordRequirements] = useState(null);

  const fetchPasswordRequirements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/password-requirements`);
      setPasswordRequirements(response.data);
    } catch (error) {
      console.error('Failed to fetch password requirements:', error);
    }
  }, []);

  const validatePasswordStrength = useCallback((password) => {
    if (!passwordRequirements) return;

    const errors = [];

    if (password.length < passwordRequirements.min_length) {
      errors.push(`At least ${passwordRequirements.min_length} characters`);
    }
    if (passwordRequirements.require_uppercase && !/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (passwordRequirements.require_lowercase && !/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (passwordRequirements.require_numbers && !/[0-9]/.test(password)) {
      errors.push('One number');
    }
    if (passwordRequirements.require_special_chars && !new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password)) {
      errors.push('One special character');
    }

    setPasswordStrength({
      isValid: errors.length === 0,
      errors
    });
  }, [passwordRequirements]);

  useEffect(() => {
    fetchPasswordRequirements();
    
    // Check if user is forced to change password
    const urlParams = new URLSearchParams(window.location.search);
    const forceChange = urlParams.get('force_change');
    const forceFlag = localStorage.getItem('force_password_change');
    
    if (forceChange === 'true' || forceFlag === 'true') {
      setForcePasswordChange(true);
      toast.error('You must change your password before accessing the application.', {
        duration: 5000
      });
      
      // Prevent navigation away
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'You must change your password before leaving this page.';
        return e.returnValue;
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [fetchPasswordRequirements]);

  // Add useEffect for password validation
  useEffect(() => {
    if (passwordForm.newPassword && passwordRequirements) {
      validatePasswordStrength(passwordForm.newPassword);
    }
  }, [passwordForm.newPassword, passwordRequirements, validatePasswordStrength]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(`${API}/profile/me`, profileForm, { headers });
      
      // Update local user object
      const updatedUser = { ...user, ...profileForm };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      login(updatedUser, token);
      
      toast.success('Profile updated successfully');
      setIsEditingProfile(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!passwordStrength.isValid) {
      toast.error('Password does not meet requirements');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Password changed successfully!');
      
      // Clear form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength({ isValid: false, errors: [] });
      
      // If this was a forced password change, clear the flag and redirect to dashboard
      if (forcePasswordChange) {
        localStorage.removeItem('force_password_change');
        toast.success('You can now access the application.');
        
        // Re-authenticate the user with AuthContext
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          login(JSON.parse(storedUser), token);
        }
        
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
          <p className="text-gray-600">Manage your account settings and security</p>
        </div>

        {/* Force Password Change Warning */}
        {forcePasswordChange && (
          <div className="mb-6 bg-red-50 border-2 border-red-500 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-red-900">Password Change Required</h3>
                <p className="text-red-700 mt-1">
                  You must change your password before you can access the application. 
                  Your current password is temporary and must be updated for security reasons.
                </p>
                <p className="text-red-600 text-sm mt-2 font-medium">
                  You cannot navigate away from this page until your password is changed.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Information Card */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5 text-blue-600" />
                <span>Profile Information</span>
              </CardTitle>
              <CardDescription>
                {isEditingProfile ? 'Update your contact information' : 'View and edit your profile'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isEditingProfile ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Username</Label>
                    <p className="mt-1 text-sm text-gray-900">{user?.username || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Email</Label>
                    <p className="mt-1 text-sm text-gray-900">{user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Role</Label>
                    <div className="mt-1 flex items-center space-x-2">
                      <Shield className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-gray-900 capitalize">
                        {user?.role?.replace('_', ' ') || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Full Name</Label>
                    <p className="mt-1 text-sm text-gray-900">{user?.name || 'Not set'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Work Phone</Label>
                    <p className="mt-1 text-sm text-gray-900">{user?.work_phone || 'Not set'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Home Phone</Label>
                    <p className="mt-1 text-sm text-gray-900">{user?.home_phone || 'Not set'}</p>
                  </div>
                  <Button 
                    onClick={() => {
                      setProfileForm({
                        name: user?.name || '',
                        work_phone: user?.work_phone || '',
                        home_phone: user?.home_phone || ''
                      });
                      setIsEditingProfile(true);
                    }}
                    className="w-full mt-4"
                  >
                    Edit Profile
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="work_phone">Work Phone</Label>
                    <Input
                      id="work_phone"
                      type="tel"
                      value={profileForm.work_phone}
                      onChange={(e) => setProfileForm({...profileForm, work_phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="home_phone">Home Phone</Label>
                    <Input
                      id="home_phone"
                      type="tel"
                      value={profileForm.home_phone}
                      onChange={(e) => setProfileForm({...profileForm, home_phone: e.target.value})}
                      placeholder="(555) 987-6543"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button type="submit" disabled={loading} className="flex-1">
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsEditingProfile(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Key className="h-5 w-5 text-blue-600" />
                <span>Change Password</span>
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Current Password */}
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showPasswords.current ? "text" : "password"}
                      placeholder="Enter your current password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    >
                      {showPasswords.current ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPasswords.new ? "text" : "password"}
                      placeholder="Create a new secure password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    >
                      {showPasswords.new ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>

                  {/* Password Requirements */}
                  {passwordRequirements && passwordForm.newPassword && (
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">Password Requirements</span>
                      </div>
                      <ul className="space-y-1 text-xs">
                        <li className={`flex items-center space-x-2 ${passwordForm.newPassword.length >= passwordRequirements.min_length ? 'text-green-600' : 'text-gray-600'}`}>
                          {passwordForm.newPassword.length >= passwordRequirements.min_length ? 
                            <CheckCircle className="h-3 w-3" /> : 
                            <div className="h-3 w-3 rounded-full border border-gray-300"></div>
                          }
                          <span>At least {passwordRequirements.min_length} characters</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${/[A-Z]/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-600'}`}>
                          {/[A-Z]/.test(passwordForm.newPassword) ? 
                            <CheckCircle className="h-3 w-3" /> : 
                            <div className="h-3 w-3 rounded-full border border-gray-300"></div>
                          }
                          <span>One uppercase letter</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${/[a-z]/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-600'}`}>
                          {/[a-z]/.test(passwordForm.newPassword) ? 
                            <CheckCircle className="h-3 w-3" /> : 
                            <div className="h-3 w-3 rounded-full border border-gray-300"></div>
                          }
                          <span>One lowercase letter</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${/[0-9]/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-600'}`}>
                          {/[0-9]/.test(passwordForm.newPassword) ? 
                            <CheckCircle className="h-3 w-3" /> : 
                            <div className="h-3 w-3 rounded-full border border-gray-300"></div>
                          }
                          <span>One number</span>
                        </li>
                        <li className={`flex items-center space-x-2 ${passwordRequirements && new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(passwordForm.newPassword) ? 'text-green-600' : 'text-gray-600'}`}>
                          {passwordRequirements && new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(passwordForm.newPassword) ? 
                            <CheckCircle className="h-3 w-3" /> : 
                            <div className="h-3 w-3 rounded-full border border-gray-300"></div>
                          }
                          <span>One special character (!@#$%^&*...)</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showPasswords.confirm ? "text" : "password"}
                      placeholder="Confirm your new password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    >
                      {showPasswords.confirm ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <p className="text-sm text-red-600">Passwords do not match</p>
                  )}
                </div>
              </form>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleChangePassword}
                className="w-full" 
                disabled={loading || !passwordStrength.isValid || passwordForm.newPassword !== passwordForm.confirmPassword}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </CardFooter>
          </Card>
          <CalendarSyncCard />
        </div>
      </div>
    </div>
  );
};

export default UserProfile;